import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { useMobileMenu } from './Footer';

const ContactForm: React.FC = () => {
  const { t, language } = useLanguage();
  const { closeMobileMenu } = useMobileMenu();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    interests: [] as string[],
    privacyConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    
    if (name === 'privacyConsent') {
      setFormData((prev) => ({ ...prev, privacyConsent: checked }));
    } else {
      setFormData((prev) => {
        if (checked) {
          return { ...prev, interests: [...prev.interests, value] };
        } else {
          return { ...prev, interests: prev.interests.filter(interest => interest !== value) };
        }
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacyConsent) {
      // Display an error or alert if the privacy policy is not accepted
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Send data to Firebase Function with updated URL
      const response = await fetch('https://sendemail-pcynyiudga-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (result.success) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
          interests: [],
          privacyConsent: false,
        });
      } else {
        console.error('Error sending email:', result.error);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const interestOptions = language === 'en' 
    ? [
        'Customer Management',
        'Document Management',
        'Quotation Creation',
        'Contract Management',
        'Template Creation',
        'Sales Process Automation',
        'Reporting and Analytics',
        'Mobile Access',
        'Worksheet Management',
        'Ticketing System',
        'Data Migration Service'
      ]
    : [
        'Ügyfélkezelés',
        'Dokumentumkezelés',
        'Árajánlatkészítés',
        'Szerződéskezelés',
        'Sablonok létrehozása',
        'Értékesítési folyamat automatizálás',
        'Jelentések és analitika',
        'Mobil hozzáférés',
        'Munkalap kezelés',
        'Jegykezelő rendszer',
        'Adatmigrációs szolgáltatás'
      ];

  return (
    <div className="bg-gray-50 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">{t('contact.title')}</h2>
          <p className="mt-4 text-lg text-gray-500">{t('contact.subtitle')}</p>
        </div>

        <div className="mt-12 max-w-3xl mx-auto">
          {submitStatus === 'success' ? (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{t('contact.success')}</p>
                </div>
              </div>
            </div>
          ) : submitStatus === 'error' ? (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{t('contact.error')}</p>
                </div>
              </div>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
            <div className="sm:col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {language === 'en' ? 'Request a Personalized Demo' : 'Kérjen személyre szabott bemutatót'}
              </h3>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                {t('contact.name')}
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="py-3 px-4 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('contact.email')}
              </label>
              <div className="mt-1">
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="py-3 px-4 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                {language === 'en' ? 'Company' : 'Cég neve'}
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="company"
                  id="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="py-3 px-4 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                {language === 'en' ? 'Phone' : 'Telefonszám'}
              </label>
              <div className="mt-1">
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="py-3 px-4 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700">
                  {language === 'en' ? 'What features are you most interested in?' : 'Mely funkciók érdeklik a legjobban?'}
                </legend>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-y-4">
                  {interestOptions.map(option => (
                    <div key={option} className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id={option}
                          name="interests"
                          value={option}
                          type="checkbox"
                          checked={formData.interests.includes(option)}
                          onChange={handleCheckboxChange}
                          className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor={option} className="text-gray-700">{option}</label>
                      </div>
                    </div>
                  ))}
                </div>
              </fieldset>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                {t('contact.message')}
              </label>
              <div className="mt-1">
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={language === 'en' ? 'Describe your specific needs or any questions you have...' : 'Írja le egyedi igényeit vagy kérdéseit...'}
                  className="py-3 px-4 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                ></textarea>
              </div>
            </div>

            <div className="sm:col-span-2">
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <input
                      id="privacyConsent"
                      name="privacyConsent"
                      type="checkbox"
                      checked={formData.privacyConsent}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="privacyConsent" className="font-medium text-gray-700">
                      {language === 'en' 
                        ? 'I have read and accept the ' 
                        : 'Elolvastam és elfogadom az '}
                      <Link 
                        to="/privacy" 
                        onClick={closeMobileMenu}
                        className="text-indigo-600 hover:text-indigo-800 underline"
                      >
                        {language === 'en' 
                          ? 'Privacy Policy' 
                          : 'Adatvédelmi Tájékoztatót'}
                      </Link>
                    </label>
                    <p className="text-gray-500 mt-1">
                      {language === 'en'
                        ? 'By submitting this form, you consent to the processing of your personal data as described in our Privacy Policy.'
                        : 'Az űrlap beküldésével hozzájárul személyes adatainak az Adatvédelmi Tájékoztatóban leírtak szerinti kezeléséhez.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={isSubmitting || !formData.privacyConsent}
                className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-md text-base font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {isSubmitting 
                  ? t('common.loading') 
                  : (language === 'en' ? 'Submit' : 'Elküldöm')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm; 